import React, { useState, useEffect, useCallback } from 'react';
import { Box, Paper, Typography, Button, Tabs, Tab, CircularProgress, Tooltip, Alert, LinearProgress } from '@mui/material';
import { Download, Refresh, CloudUpload } from '@mui/icons-material';
import Markdown from 'react-markdown';
import { proposalService, tokenManager } from '../services/api';

const SP_SITE_URL = process.env.REACT_APP_SP_SITE_URL || '';
const SP_FOLDER = process.env.REACT_APP_SP_FOLDER || 'ProposalGenerator';

export default function StepProposal({ session, setSession, onBack }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(session.proposalResult || null);
  const [activeTab, setActiveTab] = useState(0);
  const [spStatus, setSpStatus] = useState(null);
  const [shouldGenerate, setShouldGenerate] = useState(!session.proposalResult);

  const generate = useCallback(async () => {
    setLoading(true);
    setResult(null);
    try {
      const cfg = session.config || session.analysis?.config;
      const res = await proposalService.generate(session.sessionId, cfg, session.finalSummary);
      setResult(res);
      setSession(s => ({ ...s, proposalResult: res }));
    } catch (err) {
      alert('Generation failed: ' + (err.response?.data?.detail || err.message));
    } finally { setLoading(false); }
  }, [session, setSession]);

  useEffect(() => {
    if (shouldGenerate) { setShouldGenerate(false); generate(); }
  }, [shouldGenerate, generate]);

  const handleSpUpload = async () => {
    setSpStatus({ type: 'info', msg: 'Uploading proposal to SharePoint...' });
    try {
      const res = await proposalService.uploadProposalToSP(session.sessionId, SP_SITE_URL, SP_FOLDER);
      setSpStatus({ type: 'success', msg: '✅ Uploaded to SharePoint!' });
      setTimeout(() => window.open(res.web_url, '_blank'), 500);
    } catch (err) {
      setSpStatus({ type: 'error', msg: `Upload failed: ${err.response?.data?.detail || err.message}` });
    }
  };

  const handleRegenerate = () => {
    setResult(null);
    setSession(s => ({ ...s, proposalResult: null }));
    setActiveTab(0);
    setSpStatus(null);
    setShouldGenerate(true);
  };

  if (loading) {
    return (
      <Paper sx={{ p: 8, border: '1px solid #E2E8F0', textAlign: 'center' }}>
        <CircularProgress size={60} sx={{ mb: 3 }} />
        <Typography variant="h6">Generating Technical Proposal...</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Writing sections + rendering diagrams + compliance check</Typography>
        <LinearProgress sx={{ mt: 3, mx: 'auto', maxWidth: 400 }} />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>This takes 3-5 minutes. Word document is pre-built so download will be instant.</Typography>
      </Paper>
    );
  }
  if (!result) return null;

  const comp = result.compliance || {};
  const secs = result.sections || [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Compliance */}
      {!comp.parse_error && (
        <Paper sx={{ p: 3, border: '1px solid #E2E8F0' }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            📊 Compliance Check — How well does the proposal cover your RFP requirements?
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {[
              ['Coverage', `${comp.coverage_percentage || 0}%`, '#6D28D9', 'Percentage of requirements addressed'],
              ['Fully Covered', comp.covered || 0, '#16A34A', 'Requirements fully addressed'],
              ['Partially Covered', comp.partially_covered || 0, '#D97706', 'Requirements mentioned but lacking detail'],
              ['Missing', comp.missing || 0, '#DC2626', 'Requirements not addressed'],
            ].map(([label, val, color, tooltip]) => (
              <Tooltip title={tooltip} arrow key={label}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderColor: `${color}40`, cursor: 'help', flex: 1 }}>
                  <Typography variant="h3" fontWeight={800} sx={{ color }}>{val}</Typography>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                </Paper>
              </Tooltip>
            ))}
          </Box>
        </Paper>
      )}

      {/* Proposal */}
      <Paper sx={{ border: '1px solid #E2E8F0', overflow: 'hidden' }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto"
          sx={{ borderBottom: '1px solid #E2E8F0', px: 1 }}>
          <Tab label="📄 Full Proposal" />
          {secs.map((s, i) => <Tab key={s.id} label={`${i + 1}. ${s.title}`} />)}
        </Tabs>
        <Box sx={{ p: 4, maxHeight: 'calc(100vh - 380px)', overflow: 'auto',
          '& h1': { borderBottom: '1px solid #E2E8F0', pb: 1, mb: 2 },
          '& h2': { color: '#6D28D9', mt: 3 }, '& h3': { color: '#DB2777' },
          '& table': { width: '100%', borderCollapse: 'collapse', my: 2 },
          '& th, & td': { border: '1px solid #E2E8F0', p: 1.5, textAlign: 'left', fontSize: 13 },
          '& th': { bgcolor: '#F8FAFC', fontWeight: 600 },
          '& code': { bgcolor: '#F1F5F9', px: 0.5, borderRadius: 1, fontSize: 12 },
          '& li': { mb: 0.5 },
        }}>
          {activeTab === 0 ? (
            <Markdown>{result.proposal}</Markdown>
          ) : (
            <Box>
              <Typography variant="h5" fontWeight={700} gutterBottom>{secs[activeTab - 1]?.title}</Typography>
              <Markdown>{result.section_results?.[secs[activeTab - 1]?.id] || 'Not generated'}</Markdown>
            </Box>
          )}
        </Box>
      </Paper>

      {/* SharePoint status */}
      {spStatus && <Alert severity={spStatus.type} sx={{ borderRadius: 2 }}>{spStatus.msg}</Alert>}

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={onBack}>← Back</Button>
        <Button variant="contained" startIcon={<Download />}
          onClick={() => {
            window.location.href = `/api/proposal/download-docx/${session.sessionId}`;
          }}
          sx={{ flex: 1, bgcolor: '#6D28D9', '&:hover': { bgcolor: '#5B21B6' } }}>
          ⬇️ Download Word (.docx)
        </Button>
        <Button variant="contained" startIcon={<Download />}
          onClick={() => {
            window.location.href = `/api/proposal/download-drawio/${session.sessionId}`;
          }}
          sx={{ flex: 1, bgcolor: '#0891B2', '&:hover': { bgcolor: '#0E7490' } }}>
          📐 Download Draw.io (.drawio)
        </Button>
        {tokenManager.isAuthenticated() && SP_SITE_URL && (
          <Button variant="contained" startIcon={<CloudUpload />} onClick={handleSpUpload} color="success" sx={{ flex: 1 }}>
            📎 Upload to SharePoint
          </Button>
        )}
        <Button variant="outlined" startIcon={<Refresh />} color="warning" onClick={handleRegenerate} disabled={loading}>
          Regenerate
        </Button>
      </Box>
    </Box>
  );
}
